import { useMemo } from 'react';
import { UseQueryResult } from 'react-query';

import { ApiError, NumericScore, Study, StudyScoreClient } from 'jf/api';
import { useClientQueries } from 'jf/utils/useClientQuery';

import { useSequenceStudies } from './useSequenceStudies';

type UseSequenceScoresProps = {
  sequenceRef: string | undefined;
  teamRef?: string;
  type?: Parameters<typeof StudyScoreClient.getStudyScores>[0]['type'];
};

export const useSequenceScores = (
  props: UseSequenceScoresProps
): {
  scoreQueries: UseQueryResult<NumericScore[], ApiError>[];
  scores: { [ref: string]: NumericScore[] };
} => {
  const closedStudies = useSequenceStudies(props.sequenceRef, Study.status.CLOSED);

  const studyScoresQueries = useClientQueries(
    StudyScoreClient.getStudyScores,
    closedStudies?.map((study) => ({ studyRef: study.ref, type: 'TOPIC' as 'TOPIC' })) ?? [],
    { enabled: !props.teamRef }
  );

  const studyScoresForTeamQueries = useClientQueries(
    StudyScoreClient.getStudyScoresForTeam,
    closedStudies?.map((study) => ({
      studyRef: study.ref,
      type: 'TOPIC' as 'TOPIC',
      teamRef: props.teamRef!,
    })) ?? [],
    { enabled: !!props.teamRef }
  );

  const scoreMapping: { [ref: string]: NumericScore[] } = useMemo(() => {
    const queries = props.teamRef ? studyScoresForTeamQueries : studyScoresQueries;
    if (!closedStudies || !queries) {
      return {};
    }
    return queries?.reduce(
      (acc, scores, index) => ({
        ...acc,
        [closedStudies[index].ref]: scores.data!,
      }),
      {} as { [ref: string]: NumericScore[] }
    );
  }, [studyScoresQueries, closedStudies, studyScoresForTeamQueries, props.teamRef]);

  return {
    scoreQueries: props.teamRef ? studyScoresForTeamQueries : studyScoresQueries,
    scores: scoreMapping,
  };
};
